import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useSelector } from "react-redux"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import {
  downloadCandidateDocument,
  getCandidateDocuments,
  uploadCandidateDocument,
} from "../infrastructure/candidate-service"

export function useCandidateDocuments(candidateId: string) {
  const queryClient = useQueryClient()
  const organizationId = useSelector(selectCurrentOrganizationId)

  const documentsQuery = useQuery({
    queryKey: ["candidate-documents", organizationId, candidateId],
    queryFn: () => getCandidateDocuments(organizationId, candidateId),
    enabled: !!candidateId,
  })

  const uploadMutation = useMutation({
    mutationFn: ({ file, fileName }: { file: File; fileName?: string }) =>
      uploadCandidateDocument(organizationId, candidateId, file, fileName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["candidate-documents", organizationId, candidateId],
      })
    },
  })

  const downloadMutation = useMutation({
    mutationFn: (documentId: string) =>
      downloadCandidateDocument(organizationId, documentId),
  })

  return {
    documents: documentsQuery.data ?? [],
    isLoading: documentsQuery.isLoading,
    error: documentsQuery.error,
    uploadDocument: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    downloadDocument: downloadMutation.mutateAsync,
    isDownloading: downloadMutation.isPending,
  }
}
